/**
 * Per-account Djay variant selection hook.
 *
 * Scopes the selected mascotte variant to the active learner account.
 * When switching accounts, each account gets their own variant (or defaults to 'default').
 *
 * DDD co-locatie: moved from components/mascot/ in s60.
 * Fase 2 dataLayer migration: localStorage calls replaced by dataLayer.
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { DjayVariant } from './DjayAvatar'
import { useAppStore } from '../app/hooks/useAppStore'
import { dataLayer } from '../data/dataLayer'

const DEFAULT_VARIANT: DjayVariant = 'default'

export function useDjaySelection() {
  const { activeAccount } = useAppStore()
  const accountId = activeAccount?.id ?? null
  const prevAccountIdRef = useRef<string | null>(accountId)

  const [selectedVariant, setSelectedVariantState] =
    useState<DjayVariant>(DEFAULT_VARIANT)

  // Load variant from dataLayer on mount and account switch
  useEffect(() => {
    dataLayer.getDjayVariant(accountId).then((stored) => {
      setSelectedVariantState((stored as DjayVariant) || DEFAULT_VARIANT)
    })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (prevAccountIdRef.current !== accountId) {
      prevAccountIdRef.current = accountId
      dataLayer.getDjayVariant(accountId).then((stored) => {
        setSelectedVariantState((stored as DjayVariant) || DEFAULT_VARIANT)
      })
    }
  }, [accountId])

  const setSelectedVariant = useCallback(
    (variant: DjayVariant) => {
      setSelectedVariantState(variant)
      dataLayer.saveDjayVariant(
        accountId,
        variant === DEFAULT_VARIANT ? null : variant,
      )
    },
    [accountId],
  )

  return { selectedVariant, setSelectedVariant }
}
